"use strict";
import { useMutation } from "@tanstack/react-query";
import { useAccountDrawer } from "components/AccountDrawer/MiniPortfolio/hooks";
import Column from "components/deprecated/Column";
import { useAccount } from "hooks/useAccount";
import { useGroupedRecentTransfers } from "hooks/useGroupedRecentTransfers";
import { useModalState } from "hooks/useModalState";
import { useSendCallback } from "hooks/useSendCallback";
import { NewAddressSpeedBumpModal } from "pages/Swap/Send/NewAddressSpeedBump";
import SendCurrencyInputForm from "pages/Swap/Send/SendCurrencyInputForm";
import { SendRecipientForm } from "pages/Swap/Send/SendRecipientForm";
import { SendReviewModalInner } from "pages/Swap/Send/SendReviewModal";
import { SmartContractSpeedBumpModal } from "pages/Swap/Send/SmartContractSpeedBump";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSendContext } from "state/send/SendContext";
import { Button, Flex } from "ui/src";
import { useIsSmartContractAddress } from "uniswap/src/features/address/useIsSmartContractAddress";
import { useEnabledChains } from "uniswap/src/features/chains/hooks/useEnabledChains";
import Trace from "uniswap/src/features/telemetry/Trace";
import { ElementName, InterfaceEventName, ModalName } from "uniswap/src/features/telemetry/constants";
import {
  TransactionScreen,
  useTransactionModalContext
} from "uniswap/src/features/transactions/components/TransactionModal/TransactionModalContext";
function useSendButtonState() {
  const { sendState, derivedSendInfo } = useSendContext();
  const { recipient } = sendState;
  const { parsedTokenAmount, recipientData } = derivedSendInfo;
  const { t } = useTranslation();
  return useMemo(() => {
    if (recipient && !recipientData) {
      return {
        label: t("common.invalidRecipient.error"),
        disabled: true
      };
    }
    if (!parsedTokenAmount) {
      return {
        label: t("common.noAmount.error"),
        disabled: true
      };
    }
    if (!recipient && !recipientData) {
      return {
        label: t("common.input.noRecipient.error"),
        disabled: true
      };
    }
    return {
      label: t("common.send.button"),
      disabled: false
    };
  }, [t, parsedTokenAmount, recipient, recipientData]);
}
var SendFormModalState = /* @__PURE__ */ ((SendFormModalState2) => {
  SendFormModalState2["None"] = "None";
  SendFormModalState2["SMART_CONTRACT_SPEED_BUMP"] = "SMART_CONTRACT_SPEED_BUMP";
  SendFormModalState2["NEW_ADDRESS_SPEED_BUMP"] = "NEW_ADDRESS_SPEED_BUMP";
  return SendFormModalState2;
})(SendFormModalState || {});
var SendSpeedBump = /* @__PURE__ */ ((SendSpeedBump2) => {
  SendSpeedBump2["SMART_CONTRACT_SPEED_BUMP"] = "SMART_CONTRACT_SPEED_BUMP";
  SendSpeedBump2["NEW_ADDRESS_SPEED_BUMP"] = "NEW_ADDRESS_SPEED_BUMP";
  return SendSpeedBump2;
})(SendSpeedBump || {});
function SendFormInner({ disableTokenInputs = false, onCurrencyChange }) {
  const account = useAccount();
  const { t } = useTranslation();
  const { defaultChainId } = useEnabledChains();
  const { setScreen } = useTransactionModalContext();
  const accountDrawer = useAccountDrawer();
  const [sendFormModalState, setSendFormModalState] = useState("None" /* None */);
  const [sendFormSpeedBumpState, setSendFormSpeedBumpState] = useState({
    ["NEW_ADDRESS_SPEED_BUMP" /* NEW_ADDRESS_SPEED_BUMP */]: false,
    ["SMART_CONTRACT_SPEED_BUMP" /* SMART_CONTRACT_SPEED_BUMP */]: false
  });
  const { sendState, derivedSendInfo } = useSendContext();
  const { inputError, recipientData } = derivedSendInfo;
  const { isSmartContractAddress, loading: loadingSmartContractAddress } = useIsSmartContractAddress(
    recipientData?.address,
    sendState.inputCurrency?.chainId ?? defaultChainId
  );
  const { transfers: recentTransfers, loading: transfersLoading } = useGroupedRecentTransfers(account.address);
  const isRecentAddress = useMemo(() => {
    if (!recipientData?.address) {
      return void 0;
    }
    return !!recentTransfers?.[recipientData.address];
  }, [recentTransfers, recipientData?.address]);
  const sendButtonState = useSendButtonState();
  const handleModalState = useCallback((newState) => {
    setSendFormModalState(newState ?? "None" /* None */);
  }, []);
  useEffect(() => {
    setSendFormSpeedBumpState(() => ({
      ["SMART_CONTRACT_SPEED_BUMP" /* SMART_CONTRACT_SPEED_BUMP */]: isSmartContractAddress,
      ["NEW_ADDRESS_SPEED_BUMP" /* NEW_ADDRESS_SPEED_BUMP */]: !isRecentAddress
    }));
  }, [isRecentAddress, isSmartContractAddress, recipientData?.address]);
  const handleSendButton = useCallback(
    (prevSpeedBump) => {
      if (prevSpeedBump !== "SMART_CONTRACT_SPEED_BUMP" /* SMART_CONTRACT_SPEED_BUMP */ && sendFormSpeedBumpState["SMART_CONTRACT_SPEED_BUMP" /* SMART_CONTRACT_SPEED_BUMP */]) {
        handleModalState("SMART_CONTRACT_SPEED_BUMP" /* SMART_CONTRACT_SPEED_BUMP */);
        return;
      }
      if (prevSpeedBump !== "NEW_ADDRESS_SPEED_BUMP" /* NEW_ADDRESS_SPEED_BUMP */ && sendFormSpeedBumpState["NEW_ADDRESS_SPEED_BUMP" /* NEW_ADDRESS_SPEED_BUMP */]) {
        handleModalState("NEW_ADDRESS_SPEED_BUMP" /* NEW_ADDRESS_SPEED_BUMP */);
        return;
      }
      setScreen(TransactionScreen.Review);
    },
    [handleModalState, sendFormSpeedBumpState, setScreen]
  );
  const handleConfirmSmartContractSpeedBump = useCallback(() => {
    setSendFormSpeedBumpState((prev) => ({
      ...prev,
      ["SMART_CONTRACT_SPEED_BUMP" /* SMART_CONTRACT_SPEED_BUMP */]: false
    }));
    handleModalState("None" /* None */);
    handleSendButton("SMART_CONTRACT_SPEED_BUMP" /* SMART_CONTRACT_SPEED_BUMP */);
  }, [handleModalState, handleSendButton]);
  const handleCancelSmartContractSpeedBump = useCallback(
    () => handleModalState("None" /* None */),
    [handleModalState]
  );
  const handleConfirmNewAddressSpeedBump = useCallback(() => {
    setSendFormSpeedBumpState((prev) => ({
      ...prev,
      ["NEW_ADDRESS_SPEED_BUMP" /* NEW_ADDRESS_SPEED_BUMP */]: false
    }));
    handleModalState("None" /* None */);
    handleSendButton("NEW_ADDRESS_SPEED_BUMP" /* NEW_ADDRESS_SPEED_BUMP */);
  }, [handleModalState, handleSendButton]);
  const handleCancelNewAddressSpeedBump = useCallback(
    () => handleModalState("None" /* None */),
    [handleModalState]
  );
  const buttonDisabled = !!inputError || loadingSmartContractAddress || transfersLoading || sendButtonState.disabled;
  return <><Column gap="xs"><SendCurrencyInputForm disabled={disableTokenInputs} onCurrencyChange={onCurrencyChange} /><SendRecipientForm disabled={disableTokenInputs} />{account.isDisconnected ? <Trace
    logPress
    eventOnTrigger={InterfaceEventName.ConnectWalletButtonClicked}
    element={ElementName.ConnectWalletButton}
  ><Flex row><Button variant="branded" emphasis="secondary" size="large" fill onPress={accountDrawer.open}>{t("common.connectWallet.button")}</Button></Flex></Trace> : <Trace logPress element={ElementName.SendButton}><Flex row><Button
    variant="branded"
    emphasis="primary"
    size="large"
    fill
    isDisabled={buttonDisabled}
    onPress={() => handleSendButton()}
  >{sendButtonState.label}</Button></Flex></Trace>}</Column><SmartContractSpeedBumpModal
    isOpen={sendFormModalState === "SMART_CONTRACT_SPEED_BUMP" /* SMART_CONTRACT_SPEED_BUMP */}
    onConfirm={handleConfirmSmartContractSpeedBump}
    onDismiss={handleCancelSmartContractSpeedBump}
  /><NewAddressSpeedBumpModal
    isOpen={sendFormModalState === "NEW_ADDRESS_SPEED_BUMP" /* NEW_ADDRESS_SPEED_BUMP */}
    onConfirm={handleConfirmNewAddressSpeedBump}
    onDismiss={handleCancelNewAddressSpeedBump}
  /></>;
}
export function SendForm(props) {
  const { setSendState, derivedSendInfo } = useSendContext();
  const { parsedTokenAmount, recipientData, transaction, gasFee } = derivedSendInfo;
  const { closeModal } = useModalState(ModalName.Send);
  const sendCallback = useSendCallback({
    currencyAmount: parsedTokenAmount,
    recipient: recipientData?.address,
    transactionRequest: transaction,
    gasFee
  });
  const { mutate: handleSend, isPending: isConfirming } = useMutation({
    mutationFn: sendCallback,
    onSuccess: () => {
      closeModal();
      setSendState((prev) => ({
        ...prev,
        exactAmountToken: void 0,
        exactAmountFiat: "",
        recipient: "",
        validatedRecipient: void 0,
        inputInFiat: true
      }));
    }
  });
  const { screen, setScreen } = useTransactionModalContext();
  switch (screen) {
    case TransactionScreen.Form:
      return <SendFormInner {...props} />;
    case TransactionScreen.Review:
      return <SendReviewModalInner
        onConfirm={handleSend}
        onDismiss={() => setScreen(TransactionScreen.Form)}
        isConfirming={isConfirming}
      />;
    default:
      return null;
  }
}
